import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import CardTable from "../../../components/AdminTable/CardTable";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";
import { FaPrint, FaReply, FaCog, FaEdit, FaTrashAlt } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";

function Deposite() {
  const [data, setData] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDelModal, setShowDelModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [formData, setFormData] = useState({});
  const user = JSON.parse(localStorage.getItem("rms-admin"));

  const toggleDropdown = useCallback(
    (id) => {
      setOpenDropdown(openDropdown === id ? null : id);
    },
    [openDropdown]
  );

  const openReturnModal = (deposit) => {
    setSelectedDeposit(deposit);
    setShowReturnModal(true);
    setOpenDropdown(null);
  };

  const openEditModal = (deposit) => {
    setSelectedDeposit(deposit);
    setFormData(deposit);
    setShowEditModal(true);
    setOpenDropdown(null);
  };

  const openDeleteModal = (deposit) => {
    setSelectedDeposit(deposit);
    setShowDelModal(true);
    setOpenDropdown(null);
  };

  const fetchDeposits = useCallback(async () => {
    try {
      const res = await axios.get(`${apiKey}/api/rms/admin/deposit`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      if (res.status === 200 && res.data.success) {
        const formatted = res.data.data.map((item, index) => ({
          sn: index + 1,
          id: item.id,
          name: item.name,
          phone: item.phone,
          amount: item.amount,
          return_amount: item.return_amount || "-",
          date: new Date(item.date).toLocaleString(),
          description: item.description || "-",
          note: item.note || "-",
          added_by: item.user?.name || "N/A",
        }));
        setData(formatted);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  }, [user?.token]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  const handleUpdate = async (updatedData) => {
    try {
      const formattedDate = updatedData.date
        ? new Date(updatedData.date)
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
        : null;

      const payload = { ...updatedData, date: formattedDate };

      const res = await axios.put(
        `${apiKey}/api/rms/admin/deposit/${selectedDeposit.id}`,
        payload,
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Deposit updated successfully!", {
          position: "bottom-right",
          theme: "colored",
        });
        setShowEditModal(false);
        setShowReturnModal(false);
        fetchDeposits();
      } else {
        toast.error(res.data.message || "Something went wrong!", {
          position: "bottom-right",
          theme: "colored",
        });
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error(
        err.response?.data?.message || "Server error while updating deposit.",
        { position: "bottom-right", theme: "colored" }
      );
    }
  };

  const handleDelete = async () => {
    try {
      const res = await axios.delete(
        `${apiKey}/api/rms/admin/deposit/${selectedDeposit.id}`,
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );

      if (res.data.success) {
        toast.success("Deposit deleted successfully!", {
          position: "bottom-right",
          theme: "colored",
        });
        setShowDelModal(false);
        fetchDeposits();
      }
    } catch (err) {
      toast.error("Failed to delete deposit.", {
        position: "bottom-right",
        theme: "colored",
      });
    }
  };

  const columns = useMemo(
    () => [
      { header: "SN", accessorKey: "sn" },
      { header: "Name", accessorKey: "name" },
      { header: "Phone", accessorKey: "phone" },
      { header: "Amount", accessorKey: "amount" },
      { header: "Return Amount", accessorKey: "return_amount" },
      { header: "Date", accessorKey: "date" },
      { header: "Description", accessorKey: "description" },
      { header: "Note", accessorKey: "note" },
      { header: "Added By", accessorKey: "added_by" },
      {
        header: "Actions",
        accessorKey: "actions",
        cell: ({ row }) => (
          <div className="relative">
            <button
              className="text-blueGray-500 px-3 flex items-center justify-between gap-1 border rounded-full"
              onClick={() => toggleDropdown(row.original.id)}
            >
              <FaCog className="text-d-bg-text" />
              <IoIosArrowDown className="text-d-bg-text" />
            </button>

            {openDropdown === row.original.id && (
              <div className="absolute top-4 left-0 -translate-x-[75%] bg-white text-base z-10 float-left py-2 list-none text-left rounded shadow-lg min-w-40">
                <button
                  onClick={() => openReturnModal(row.original)}
                  className="text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap text-blueGray-700 hover:bg-gray-100"
                >
                  <FaReply />
                  Return
                </button>
                <button
                  onClick={() => openEditModal(row.original)}
                  className="text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap text-blueGray-700 hover:bg-gray-100"
                >
                  <FaEdit />
                  Edit
                </button>
                <button
                  onClick={() => openDeleteModal(row.original)}
                  className="text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap text-blueGray-700 hover:bg-gray-100"
                >
                  <FaTrashAlt />
                  Delete
                </button>
                <button
                  onClick={() => handlePrint(row.original)}
                  className="text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap text-blueGray-700 hover:bg-gray-100"
                >
                  <FaPrint /> Print Invoice
                </button>
              </div>
            )}
          </div>
        ),
      },
    ],
    [openDropdown, toggleDropdown]
  );

  const handlePrint = (deposit) => {
    const printWindow = window.open("", "_blank", "width=400,height=600");

    const htmlContent = `
    <html>
      <head>
        <title>Deposit Invoice</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          body {
            font-family: 'Courier New', monospace;
            width: 80mm;
            padding: 10px;
            font-size: 13px;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .line { border-bottom: 1px dashed #000; margin: 6px 0; }
          .info { display: flex; justify-content: space-between; }
          .footer { margin-top: 10px; text-align: center; font-size: 12px; }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        <div class="center bold">Deposit Invoice</div>
        <div class="line"></div>

        <div class="info"><span>Name:</span><span>${deposit.name}</span></div>
        <div class="info"><span>Phone:</span><span>${deposit.phone}</span></div>
        <div class="info"><span>Amount:</span><span>${
          deposit.amount
        }</span></div>
        <div class="info"><span>Return:</span><span>${
          deposit.return_amount || "-"
        }</span></div>
        <div class="info"><span>Date:</span><span>${new Date(
          deposit.date
        ).toLocaleString()}</span></div>
        <div class="line"></div>

        <div><strong>Description:</strong> ${deposit.description || "-"}</div>
        <div><strong>Note:</strong> ${deposit.note || "-"}</div>
        <div class="line"></div>

        <div class="info"><span>Added By:</span><span>${
          deposit.added_by || "-"
        }</span></div>

        <div class="footer">Thank you!</div>
      </body>
    </html>
  `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <main className="relative">
      <CardTable data={data} columns={columns} heading="Deposit" />

      {/* Return Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded p-5 w-96 shadow-lg">
            <h2 className="text-lg font-semibold mb-3 text-center">
              Return Deposit
            </h2>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={selectedDeposit?.name || ""}
                className="border p-2 w-full rounded bg-gray-100"
                readOnly
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="text"
                value={selectedDeposit?.phone || ""}
                className="border p-2 w-full rounded bg-gray-100"
                readOnly
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Return Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={
                  formData.return_amount || selectedDeposit?.return_amount || ""
                }
                onChange={(e) =>
                  setFormData({ ...formData, return_amount: e.target.value })
                }
                className="border p-2 w-full rounded"
                placeholder="Enter return amount"
                required
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowReturnModal(false)}
                className="bg-gray-300 text-white px-3 py-1 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdate(formData)}
                className="bg-d-cat-btn text-white px-3 py-1 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h1 className="text-xl font-semibold mb-4 text-center">
              Edit Deposit
            </h1>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium">Name</label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Phone</label>
                <input
                  type="text"
                  value={formData.phone || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Amount</label>
                <input
                  type="number"
                  value={formData.amount || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Date</label>
                <input
                  type="datetime-local"
                  value={
                    formData.date
                      ? new Date(formData.date).toISOString().slice(0, 16)
                      : ""
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Description</label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Note</label>
                <textarea
                  value={formData.note || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, note: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-300 text-white rounded hover:bg-gray-400"
              >
                Close
              </button>
              <button
                onClick={() => handleUpdate(formData)}
                className="px-4 py-2 bg-d-cat-btn text-white rounded"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDelModal && (
        <div
          className="w-full h-screen grid place-items-center fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-md p-4 z-10"
          onClick={() => setShowDelModal(false)}
        >
          <div
            className="p-4 rounded-lg bg-white max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className="text-[#774d96] font-semibold text-xl text-center">
              Alert!
            </h1>
            <p className="text-center text-gray-700">
              Are you sure you want to delete this deposit?
            </p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDelModal(false)}
                className="bg-gray-400 text-white px-4 py-1 rounded"
              >
                No
              </button>
              <button
                onClick={handleDelete}
                className="bg-d-cat-btn text-white px-4 py-1 rounded"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Deposite;
